import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/Button/Button';
import Input from '../../../../../components/Input/Input';
import Radio from '../../../../../components/Radio/Radio';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel } from '../../../../conversion';
import { selectProject, selectResults } from '../../../selector/conversion';
import { IProjectMacra1, setHydraulicData } from '../../../store/project/projectSlice';
import { cleanResults, setDischargeAlert } from '../../../store/project/resultsSlice';
import './HydraulicData.scss';
import { IHydraulicDataProps } from './IHydraulicData';
import logoCurvedLeft from './images/hydraulic_data_curved.png';
import logoCurvedRight from './images/hydraulic_data_curved_right.png';
import logoStraight from './images/hydraulic_data_straight.png';
import ModalSoilVelocity from './ModalSoilVelocity/ModalSoilVelocity';
import { DischargeType, Shape, SideOrientation } from '../../../enum/HydraulicData';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { UnitOfMeasure } from '../../../../enum/project';
import { roundToFour, roundToTwo } from '../Geometry/utils';
import * as BeMaccaferriCalculationHelper from '../../../api/calculation/BeMaccaferriCalculationHelper';
import { debounce } from 'lodash';

const HydraulicDataDefault = {
    dischargeType: DischargeType.Automated,
    discharge: 0,
    freeboard: 0,
    longitudinalSlope: 0,
    soilAdmissibleVelocity: 0,
    shape: Shape.Straight,
    radius: 0,
    sideOrientation: SideOrientation.Left,
};

const HydraulicData = (props: IHydraulicDataProps) => {
    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);
    const result = useAppSelector(selectResults);
    const { t } = useTranslation();
    let timer = React.useRef<NodeJS.Timeout | null>(null);

    let HydraulicDataPayload = HydraulicDataDefault;
    if (!!project.data && project.data.hydraulicData) {
        HydraulicDataPayload = project.data.hydraulicData;
    }
    const [soilAdmissibleVelocity, setSoilAdmissibleVelocity] = React.useState<number>(HydraulicDataPayload.soilAdmissibleVelocity);
    const [disableDischarge, setDisableDischarge] = React.useState<boolean>(project.data?.hydraulicData?.dischargeType === DischargeType.Automated);
    const [disableRadiusText, setDisableRadiusText] = React.useState<boolean>(project.data?.hydraulicData?.shape == Shape.Straight);
    const [disableLeftSide, setDisableLeftSide] = React.useState<boolean>(project.data?.hydraulicData?.shape == Shape.Straight);
    const [disableRightSide, setDisableRightSide] = React.useState<boolean>(project.data?.hydraulicData?.shape == Shape.Straight);
    const [styleCurved, setStyleCurved] = React.useState<React.CSSProperties | undefined>(project.data?.hydraulicData?.shape == Shape.Curved ? { opacity: '1' } : { opacity: '0.5' });
    const [open, setOpen] = React.useState<boolean>(false);
    const [maxDischarge, setMaxDischarge] = React.useState<number>(0);
    const dispatch = useAppDispatch();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (!!e) {
            let target = e.target;
            let name = target.name;
            let value = parseFloat(target.value);
            if (name === 'longitudinalSlope' && value > 100) value = 100;
            if (name === 'longitudinalSlope' && (value < 0.001 || isNaN(value))) value = 0.001;
            if (name === 'radius' && (value < 1 || isNaN(value))) value = 1;
            HydraulicDataPayload = {
                ...project.data?.hydraulicData!,
                [name]: value,
            };
            if (timer.current) {
                clearTimeout(timer.current);
            }
            if (name !== 'dischargeType' && name !== 'discharge') dispatch(setDischargeAlert(false));
            timer.current = setTimeout(() => {
                dispatch(cleanResults());
                dispatch(setHydraulicData(HydraulicDataPayload));
            }, 1000);
        }
    };

    const handleDischarge = async (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.target.value) {
            setDisableDischarge(false);
            handleInputChange(e);
            await getResultsForDischarge(HydraulicDataPayload.discharge, project.data!, true);
        }
    };

    const handleAutoDischarge = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.target.value) {
            setDisableDischarge(true);
            handleInputChange(e);
        }
    };

    const handleVelocityButton = (e: React.MouseEvent<HTMLButtonElement> | undefined) => {
        setOpen(true);
    };

    const handleStraight = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.target.value) {
            setStyleCurved({ opacity: '0.5' });
            setDisableRadiusText(true);
            setDisableLeftSide(true);
            setDisableRightSide(true);
            handleInputChange(e);
        }
    };

    const handleCurved = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.target.value) {
            setStyleCurved({ opacity: '1' });
            setDisableRadiusText(false);
            setDisableLeftSide(false);
            setDisableRightSide(false);
            handleInputChange(e);
        }
    };

    const handleSoilAdmissibleVelocity = (velocity: number) => {
        HydraulicDataPayload = {
            ...project.data?.hydraulicData!,
            soilAdmissibleVelocity: velocity,
        };
        dispatch(cleanResults());
        dispatch(setHydraulicData(HydraulicDataPayload));
    };

    const handleClose = (velocity: number) => {
        setSoilAdmissibleVelocity(velocity);
        handleSoilAdmissibleVelocity(velocity);
        setOpen(false);
    };

    React.useEffect(() => {
        if (
            !result.initialState &&
            !!result.data &&
            !!result.data.generalResult.general.discharge &&
            HydraulicDataPayload.dischargeType === DischargeType.Manual &&
            HydraulicDataPayload.discharge > result.data.generalResult.general.discharge
        ) {
            setInputMaxDischarge();
        }
    }, [result, HydraulicDataPayload]);

    const getResultsForDischarge = React.useCallback(async (discharge: number, projectUpdated: IProjectMacra1, dischargeCheck: boolean = false) => {
        if (!!projectUpdated && projectUpdated.hydraulicData) {
            try {
                const results = await BeMaccaferriCalculationHelper.getResultsAsync({
                    ...projectUpdated,
                    hydraulicData: { ...projectUpdated.hydraulicData, discharge: discharge, dischargeType: DischargeType.Automated },
                });
                dispatch(setDischargeAlert(discharge > results.data.generalResult.general.discharge));
                setMaxDischarge(roundToTwo(results.data.generalResult.general.discharge));
            } catch (e) {}
        }
    }, []);

    const getResultDebounce = React.useRef(
        debounce(async (value, project) => {
            await getResultsForDischarge(value, project);
        }, 1000),
    ).current;

    React.useEffect(() => {
        return () => {
            getResultDebounce.cancel();
        };
    }, [getResultDebounce]);

    const setInputMaxDischarge = React.useCallback(
        async (max?: number) => {
            HydraulicDataPayload = {
                ...HydraulicDataPayload,
                dischargeType: DischargeType.Automated,
                discharge: max ?? HydraulicDataPayload.discharge,
            };
            setDisableDischarge(true);
            if (timer.current) {
                clearTimeout(timer.current);
            }
            timer.current = setTimeout(() => {
                dispatch(setHydraulicData(HydraulicDataPayload));
            }, 1000);
        },
        [HydraulicDataPayload],
    );

    return (
        <div className='hydraulicDataContainer'>
            <div className='d-flex justify-content-between'>
                <Radio
                    title={t('hydraulic_data_discharge', { unit_measure: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number] })}
                    name='dischargeType'
                    value={DischargeType.Manual}
                    checked={project.data?.hydraulicData?.dischargeType === DischargeType.Manual}
                    onChange={handleDischarge}
                />
                <Input
                    type='number'
                    step={0.01}
                    title=''
                    name='discharge'
                    className='d-flex justify-content-end'
                    defaultValue={HydraulicDataPayload.discharge}
                    disabled={disableDischarge}
                    onChange={e => {
                        getResultDebounce(parseFloat(e.target.value), project.data);

                        handleInputChange(e);
                    }}
                />
            </div>
            <Radio
                title={t('hydraulic_data_automated_discharge')}
                name='dischargeType'
                value={DischargeType.Automated}
                checked={project.data?.hydraulicData?.dischargeType === DischargeType.Automated}
                onChange={handleAutoDischarge}
            />
            <hr />
            <div className='d-flex flex-column gap-2'>
                <Input
                    type='number'
                    step={0.01}
                    title={t('hydraulic_data_freeboard', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                    name='freeboard'
                    className='d-flex justify-content-between align-items-center'
                    defaultValue={HydraulicDataPayload.freeboard}
                    onChange={handleInputChange}
                />
                <Input
                    type='number'
                    title={t('hydraulic_data_longitudinal_slope', { unit_measure: '%' })}
                    name='longitudinalSlope'
                    className='d-flex justify-content-between align-items-center'
                    defaultValue={HydraulicDataPayload.longitudinalSlope}
                    onChange={handleInputChange}
                />
                <div className='d-flex align-items-center'>
                    <Input
                        type='number'
                        step={0.01}
                        title={t('hydraulic_data_soil_velocity', { unit_measure: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number] })}
                        name='soilAdmissibleVelocity'
                        className='d-flex justify-content-between align-items-center'
                        classNameInput='velocityText'
                        value={soilAdmissibleVelocity}
                        onChange={e => handleClose(roundToFour(parseFloat(e.target.value)))}
                    />
                    <Button classNameBtn='btn-velocity' onClick={handleVelocityButton}>
                        <i className='fa-solid fa-sliders fs-6' />
                    </Button>

                    <ModalSoilVelocity open={open} onClose={handleClose} />
                </div>
            </div>
            <hr />
            <div className='shape-contaner'>
                <div className='d-flex flex-column'>
                    <Radio
                        title={t('hydraulic_data_shape_straight')}
                        name='shape'
                        value={Shape.Straight}
                        defaultChecked={project.data?.hydraulicData?.shape == Shape.Straight}
                        onChange={handleStraight}
                    />
                    <img className='p-3' src={logoStraight} alt='Logo Straight' />
                </div>
                <div className='curved'>
                    <Radio title={t('hydraulic_data_shape_curved')} name='shape' value={Shape.Curved} defaultChecked={project.data?.hydraulicData?.shape == Shape.Curved} onChange={handleCurved} />
                    {project.data?.hydraulicData?.sideOrientation === SideOrientation.Left ? (
                        <img className='p-3' src={logoCurvedLeft} alt='Logo Curved' />
                    ) : (
                        <img className='p-3' src={logoCurvedRight} alt='Logo Curved' />
                    )}
                    <div style={{ ...styleCurved }}>
                        <Input
                            type='number'
                            step={0.01}
                            title={t('hydraulic_data_radius', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                            name='radius'
                            classNameInput='radius'
                            defaultValue={HydraulicDataPayload.radius}
                            disabled={disableRadiusText}
                            onChange={handleInputChange}
                        />
                        <div className='pt-3'>
                            <Radio
                                title={t('hydraulic_data_side_left')}
                                name='sideOrientation'
                                value={SideOrientation.Left}
                                defaultChecked={project.data?.hydraulicData?.sideOrientation == SideOrientation.Left}
                                disabled={disableLeftSide}
                                onChange={handleInputChange}
                            />
                            <Radio
                                title={t('hydraulic_data_side_right')}
                                name='sideOrientation'
                                value={SideOrientation.Right}
                                defaultChecked={project.data?.hydraulicData?.sideOrientation == SideOrientation.Right}
                                disabled={disableRightSide}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HydraulicData;
